import { useCallback, useEffect, useRef, useState } from "react";
import { getApiJson, postApiForm } from "../services/api";
import { getAuthToken } from "../services/auth";
import { getGuestId } from "../lib/guestId";
import {
  isDirection,
  isScript,
  swapDirection,
  withFrom,
  withTo,
} from "../utils/direction";
import { storage } from "../lib/storage";

// Route translation status/rate/cancel/download through the same-origin Nitro
// /api proxy. Direct calls to the API host are blocked by the CSP connect-src
// and produce mixed-content download links; the proxy keeps everything on
// 'self' and works in every environment.
const API_PROXY = "/api";

// Backend may return absolute URLs (http(s)://api.host/api/...); strip the
// origin so the file downloads through the same-origin proxy too.
function toProxyPath(url: string): string {
  return url.replace(/^https?:\/\/[^/]+/i, "");
}

export interface UseSidePanelOptions {
  selectedDirection: string;
  setSelectedDirection: (direction: string) => void;
  setInputText: (text: string) => void;
  setOutputText: (text: string) => void;
  outputText: string;
  readOnly?: boolean;
  isLoading: boolean;
  handleTranslit: () => void;
  isAutoDetected?: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export function useSidePanel({
  selectedDirection,
  setSelectedDirection,
  setInputText,
  setOutputText,
  outputText,
  readOnly = false,
  isLoading,
  handleTranslit,
  isAutoDetected = false,
  t,
}: UseSidePanelOptions) {
  const [uploadState, setUploadState] = useState<
    "idle" | "uploading" | "error" | "success"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  // True after the server returns 401 auth_required (free guest quota used up).
  const [authRequired, setAuthRequired] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [telegramBotLink, setTelegramBotLink] = useState("");
  const [translatedFilename, setTranslatedFilename] = useState("");
  const [uploadingMessage, setUploadingMessage] = useState(
    t("sidePanel.uploading"),
  );
  const [progress, setProgress] = useState(0);
  const [jobId, setJobId] = useState<string | number | null>(null);
  const [jobToken, setJobToken] = useState<string | null>(null);
  const [completedJobId, setCompletedJobId] = useState<string | number | null>(
    null,
  );
  const [completedJobToken, setCompletedJobToken] = useState<string | null>(
    null,
  );
  const [isRated, setIsRated] = useState(false);
  const [ratingChoice, setRatingChoice] = useState<"yes" | "no" | null>(null);
  const statusIntervalRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectedDirectionRef = useRef(selectedDirection);
  const isUploadingRef = useRef(false);
  const hasRestoredRef = useRef(false);
  const jobTokenRef = useRef<string | null>(null);
  selectedDirectionRef.current = selectedDirection;

  const triggerFilePicker = useCallback(() => {
    // Block if: readOnly, already uploading, or active job exists
    if (readOnly || isUploadingRef.current || jobId) return;
    fileInputRef.current?.click();
  }, [readOnly, jobId]);

  /**
   * Clear the result of a finished translation so a newly picked file starts
   * from a clean slate. Without this the derived UI state stays on "success"
   * and the next file is silently ignored.
   */
  const resetUpload = useCallback(() => {
    if (isUploadingRef.current || jobId) return;
    setUploadState("idle");
    setErrorMessage("");
    setSubscriptionRequired(false);
    setAuthRequired(false);
    setDownloadUrl("");
    setTelegramBotLink("");
    setTranslatedFilename("");
    setUploadingMessage(t("sidePanel.uploading"));
    setProgress(0);
    setCompletedJobId(null);
    setCompletedJobToken(null);
    setIsRated(false);
    setRatingChoice(null);
  }, [jobId, t]);

  const checkJobStatus = useCallback(
    async (id: string, token: string) => {
      if (readOnly) return;
      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["X-Translation-Token"] = token;
        }
        const authToken = getAuthToken();
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        // Token also in the URL: the dev proxy drops the X-Translation-Token
        // header, and the route accepts {token?}. Header kept for direct/prod calls.
        const statusUrl = token
          ? `${API_PROXY}/translation/status/${id}/${encodeURIComponent(token)}`
          : `${API_PROXY}/translation/status/${id}`;
        const res = await fetch(statusUrl, {
          method: "GET",
          headers,
          credentials: "include",
        });
        const data = (await res.json()) as {
          success: boolean;
          status:
            | "pending"
            | "processing"
            | "completed"
            | "failed"
            | "cancelled";
          translated_file?: { url: string; name?: string };
          telegram_bot_link?: string;
          download_url?: string;
          error?: string;
          message?: string;
          progress?: number;
          mod?: string;
        };

        if (data.success) {
          if (data.status === "completed") {
            setUploadState("success");
            setProgress(100);
            isUploadingRef.current = false;
            // Update dropdown and save to localStorage when translation completes
            if (isDirection(data.mod)) {
              setSelectedDirection(data.mod);
              if (typeof window !== "undefined") {
                storage.setItem("lastTranslationMod", data.mod);
              }
            }
            // if (data.telegram_bot_link) { // vaqtinchalik o'chirilgan
            //   setTelegramBotLink(data.telegram_bot_link);
            //   setDownloadUrl("");
            // } else
            if (data.translated_file && data.translated_file.url) {
              setDownloadUrl(toProxyPath(data.translated_file.url));
              setTelegramBotLink("");
            } else if (data.download_url) {
              setDownloadUrl(toProxyPath(data.download_url));
              setTelegramBotLink("");
            }
            if (data.translated_file && data.translated_file.name) {
              setTranslatedFilename(data.translated_file.name);
            }
            if (statusIntervalRef.current) {
              window.clearInterval(statusIntervalRef.current);
              statusIntervalRef.current = null;
            }
            if (typeof window !== "undefined") {
              storage.removeItem(`file_translation_${id}`);
            }
            // Save completed job info for rating
            setCompletedJobId(id);
            setCompletedJobToken(jobTokenRef.current);
            // Reset job to allow new uploads without page refresh
            setJobId(null);
            setJobToken(null);
            jobTokenRef.current = null;
          } else if (data.status === "failed" || data.status === "cancelled") {
            setUploadState("error");
            setErrorMessage(
              data.status === "cancelled"
                ? t("sidePanel.cancelled") || "Tarjima bekor qilindi"
                : data.error || t("sidePanel.translationError"),
            );
            isUploadingRef.current = false;
            if (statusIntervalRef.current) {
              window.clearInterval(statusIntervalRef.current);
              statusIntervalRef.current = null;
            }
            if (typeof window !== "undefined") {
              storage.removeItem(`file_translation_${id}`);
            }
            // Reset job to allow retrying upload
            setJobId(null);
            setJobToken(null);
            jobTokenRef.current = null;
          } else if (
            data.status === "processing" ||
            data.status === "pending"
          ) {
            const pct = data.progress ?? 0;
            setProgress(pct);
            if (data.message) {
              setUploadingMessage(data.message);
            } else if (pct > 0) {
              setUploadingMessage(t("sidePanel.translating") + ` ${pct}%`);
            }
          }
        }
      } catch {
        // Ignore transient errors - will retry on next interval
      }
    },
    [readOnly, t],
  );

  const startStatusChecking = useCallback(
    (id: string, token: string) => {
      if (readOnly) return;
      if (statusIntervalRef.current) {
        window.clearInterval(statusIntervalRef.current);
      }

      checkJobStatus(id, token);
      statusIntervalRef.current = window.setInterval(() => {
        checkJobStatus(id, token);
      }, 3000);
    },
    [readOnly, checkJobStatus],
  );

  const uploadAndTranslate = useCallback(
    async (file: File) => {
      if (readOnly) return;
      const formData = new FormData();
      formData.append("file", file);
      formData.append("mod", selectedDirectionRef.current);

      setIsRated(false);
      setRatingChoice(null);
      setCompletedJobId(null);
      setCompletedJobToken(null);
      setUploadState("uploading");
      setUploadingMessage(t("sidePanel.uploading"));
      setProgress(0);
      setSubscriptionRequired(false);
      setAuthRequired(false);
      isUploadingRef.current = true;

      try {
        // Guest (not logged in) → send the browser guest id so the backend can
        // allow the first free translation. Logged-in requests carry the auth
        // token via getAuthHeaders() and skip the guest limit entirely.
        const guestHeaders: Record<string, string> = {};
        if (!getAuthToken()) {
          const gid = getGuestId();
          if (gid) guestHeaders["X-Guest-Id"] = gid;
        }

        const data = await postApiForm<{
          success: boolean;
          translated_file?: { url: string; name?: string };
          job_id?: string | number;
          token?: string;
          message?: string;
        }>("/translateFile", formData, { headers: guestHeaders });

        if (data.success) {
          if (data.translated_file) {
            setUploadState("success");
            setDownloadUrl(toProxyPath(data.translated_file.url));
            setTranslatedFilename(data.translated_file.name || "");
            isUploadingRef.current = false;
            return;
          }

          setUploadingMessage(t("sidePanel.translating"));
          if (data.job_id !== undefined) {
            setJobId(data.job_id);
          }
          if (data.token !== undefined) {
            setJobToken(data.token);
            jobTokenRef.current = data.token;
          }
          if (data.job_id !== undefined && data.token) {
            if (typeof window !== "undefined") {
              storage.setItem(
                `file_translation_${data.job_id}`,
                data.token,
              );
            }
            startStatusChecking(String(data.job_id), data.token);
          }
        } else {
          setUploadState("error");
          setErrorMessage(data.message || t("sidePanel.translationError"));
          isUploadingRef.current = false;
        }
      } catch (error: unknown) {
        setUploadState("error");
        isUploadingRef.current = false;
        const code = (error as any)?.body?.error;
        if (code === "auth_required") {
          // Free guest quota used up — prompt login for further translations.
          setAuthRequired(true);
          setErrorMessage("");
        } else if (
          code === "subscription_required" ||
          code === "email_not_verified"
        ) {
          setSubscriptionRequired(true);
          setErrorMessage("");
        } else if (error instanceof Error) {
          if (error.message.includes("413")) {
            setErrorMessage(t("sidePanel.fileTooLarge"));
          } else if (error.message.includes("401")) {
            setErrorMessage("Fayl tarjima qilish uchun tizimga kiring");
          } else {
            setErrorMessage(error.message || t("sidePanel.uploadError"));
          }
        } else {
          setErrorMessage(t("sidePanel.uploadError"));
        }
      }
    },
    [readOnly, selectedDirection, t, startStatusChecking],
  );

  const onFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      if (isUploadingRef.current) return;
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setUploadState("error");
        setErrorMessage(t("sidePanel.fileTooLarge"));
        return;
      }

      uploadAndTranslate(file);
    },
    [readOnly, t, uploadAndTranslate],
  );

  const cancelTranslation = useCallback(async () => {
    const id = jobId;
    const token = jobTokenRef.current || jobToken;
    if (!id || !token || readOnly) return;
    try {
      const headers: Record<string, string> = {};
      if (token) {
        headers["X-Translation-Token"] = token;
      }
      const authToken = getAuthToken();
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const res = await fetch(
        `${API_PROXY}/translation/cancel/${id}/${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers,
          credentials: "include",
        },
      );
      const data = await res.json();
      if (data.success || res.status === 200) {
        if (statusIntervalRef.current) {
          window.clearInterval(statusIntervalRef.current);
          statusIntervalRef.current = null;
        }
        if (typeof window !== "undefined") {
          storage.removeItem(`file_translation_${id}`);
        }
        setUploadState("idle");
        setJobId(null);
        setJobToken(null);
        jobTokenRef.current = null;
        setProgress(0);
        setUploadingMessage(t("sidePanel.uploading"));
        isUploadingRef.current = false;
      } else {
        setErrorMessage(data.message || "Bekor qilish amalga oshmadi");
      }
    } catch (e) {
      setErrorMessage((e as Error).message || "Bekor qilishda xatolik");
    }
  }, [jobId, jobToken, readOnly, t]);

  useEffect(() => {
    if (readOnly || typeof window === "undefined" || hasRestoredRef.current)
      return;
    const keys = storage.keys().filter((k) =>
      k.startsWith("file_translation_"),
    );
    if (keys.length === 0) return;
    hasRestoredRef.current = true;
    const jobIdKey = keys[0];
    const id = jobIdKey.replace("file_translation_", "");
    const token = storage.getItem(jobIdKey);
    if (!token) return;
    setJobId(id);
    setJobToken(token);
    jobTokenRef.current = token;
    setUploadState("uploading");
    setUploadingMessage(t("sidePanel.translating"));
    isUploadingRef.current = true;
    checkJobStatus(id, token);
    statusIntervalRef.current = window.setInterval(() => {
      checkJobStatus(id, token);
    }, 3000);
    return () => {
      if (statusIntervalRef.current) {
        window.clearInterval(statusIntervalRef.current);
      }
    };
  }, [readOnly, t, checkJobStatus]);

  useEffect(() => {
    return () => {
      if (statusIntervalRef.current) {
        window.clearInterval(statusIntervalRef.current);
      }
    };
  }, []);

  // When the user logs in, clear a leftover guest "auth required" block so the
  // upload UI comes back instead of staying stuck on the login prompt.
  useEffect(() => {
    const onAuthChange = () => {
      if (!getAuthToken()) return;
      setAuthRequired(false);
      setSubscriptionRequired(false);
      if (!isUploadingRef.current && !jobId) {
        setUploadState("idle");
        setErrorMessage("");
      }
    };
    window.addEventListener("lotin:auth", onAuthChange);
    return () => window.removeEventListener("lotin:auth", onAuthChange);
  }, [jobId]);

  const handleDownload = useCallback(async () => {
    if (!downloadUrl || readOnly) return;

    try {
      const filename =
        translatedFilename || downloadUrl.split("/").pop() || "translated_file";

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (error) {
      console.error("Download error:", error);
      setErrorMessage(t("sidePanel.downloadErrorRetry"));
      setUploadState("error");
    }
  }, [downloadUrl, translatedFilename, t, readOnly]);

  const handleRating = useCallback(
    async (choice: "yes" | "no") => {
      const ratingJobId = completedJobId || jobId;
      const ratingToken = completedJobToken || jobToken;
      if (!ratingJobId || readOnly) return;

      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (ratingToken) {
          headers["X-Translation-Token"] = ratingToken;
        }
        const authToken = getAuthToken();
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        const rateUrl = ratingToken
          ? `${API_PROXY}/translation/rate/${ratingJobId}/${encodeURIComponent(ratingToken)}`
          : `${API_PROXY}/translation/rate/${ratingJobId}`;
        const response = await fetch(
          rateUrl,
          {
            method: "POST",
            headers,
            credentials: "include",
            body: JSON.stringify({
              choice,
              comment: "",
            }),
          },
        );

        const data = await response.json();

        if (data.success) {
          setIsRated(true);
          setRatingChoice(choice);
        } else {
          console.error("Rating error:", data.message);
        }
      } catch (error) {
        console.error("Rating error:", error);
      }
    },
    [completedJobId, completedJobToken, jobId, jobToken, readOnly],
  );

  const handleDirectionSwap = useCallback(() => {
    if (readOnly) return;
    setOutputText("");
    setInputText(outputText);
    const newDirection = swapDirection(selectedDirection);
    setSelectedDirection(newDirection);
    // Update localStorage when user swaps direction
    if (typeof window !== "undefined") {
      storage.setItem("lastTranslationMod", newDirection);
    }
  }, [
    outputText,
    readOnly,
    selectedDirection,
    setInputText,
    setOutputText,
    setSelectedDirection,
  ]);

  const handleFromLanguageChange = useCallback(
    (key: React.Key | null) => {
      if (readOnly || !isScript(key)) return;
      const newDirection = withFrom(selectedDirection, key);
      setSelectedDirection(newDirection);
      // Update localStorage when user manually changes direction
      if (typeof window !== "undefined") {
        storage.setItem("lastTranslationMod", newDirection);
      }
    },
    [readOnly, selectedDirection, setSelectedDirection],
  );

  const handleToLanguageChange = useCallback(
    (key: React.Key | null) => {
      if (readOnly || !isScript(key)) return;
      const newDirection = withTo(selectedDirection, key);
      setSelectedDirection(newDirection);
      // Update localStorage when user manually changes direction
      if (typeof window !== "undefined") {
        storage.setItem("lastTranslationMod", newDirection);
      }
    },
    [readOnly, selectedDirection, setSelectedDirection],
  );

  const effectiveUploadState = readOnly ? "idle" : uploadState;
  // Block uploads while one is in progress or if there's an active job
  const isUploadingOrActive = isUploadingRef.current || !!jobId;

  return {
    t,
    readOnly,
    selectedDirection,
    handleFromLanguageChange,
    handleToLanguageChange,
    handleDirectionSwap,
    handleTranslit,
    isLoading,
    uploadState: effectiveUploadState,
    fileInputRef,
    onFileSelected,
    triggerFilePicker,
    resetUpload,
    startUpload: uploadAndTranslate,
    uploadingMessage,
    errorMessage,
    subscriptionRequired,
    authRequired,
    downloadUrl,
    telegramBotLink,
    handleDownload,
    translatedFilename,
    isRated,
    ratingChoice,
    handleRating,
    outputText,
    isAutoDetected,
    setIsRated,
    setRatingChoice,
    cancelTranslation,
    isUploadingOrActive,
  };
}
